import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Table,
  Typography,
  Menu,
  Dropdown,
  Row,
  Col,
  Card,
  Statistic,
  Popconfirm,
  Progress,
  Tag,
  List,
  Spin,
} from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ITaskOrder, TaskOrderStatus } from "../../types/taskOrder.type";
import { api } from "../../api/api";
import useGetRequest from "../../hooks/useGetRequest";
import { Produce } from "../../types/produce.type";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";
import { canEdit } from "../../utils/roleUtils";
import { Currency } from "../../types/currency.type";
import { Client } from "../../types/client.type";
import {
  DownOutlined,
  DragOutlined,
  UpOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const ACTIVE_STATUSES = [
  TaskOrderStatus.Pending,
  TaskOrderStatus.InProgress,
  TaskOrderStatus.OnHold,
];

interface SortableOrderRowProps {
  order: ITaskOrder;
  index: number;
  statusColors: Record<TaskOrderStatus, string>;
}

function SortableOrderRow({ order, index, statusColors }: SortableOrderRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: order._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <List.Item
        style={{
          border: isDragging ? "2px dashed #1890ff" : undefined,
          background: isDragging ? "#e6f7ff" : undefined,
          borderRadius: 8,
          marginBottom: 8,
          padding: "12px 16px",
        }}
        actions={[
          <span key="num" style={{ fontWeight: 600, minWidth: 28 }}>
            №{index + 1}
          </span>,
        ]}
      >
        <List.Item.Meta
          avatar={
            <div
              {...attributes}
              {...listeners}
              style={{ cursor: "grab", padding: 8, marginRight: 8 }}
            >
              <DragOutlined style={{ color: "#999", fontSize: 18 }} />
            </div>
          }
          title={
            <Space wrap>
              <Text strong>{order.clientName}</Text>
              <Tag color={statusColors[order.status] || "default"}>{order.status}</Tag>
            </Space>
          }
          description={
            <Space>
              <Text type="secondary">{order.produceName}</Text>
              <Text type="secondary">•</Text>
              <Text type="secondary">{order.total} шт</Text>
              {order.admissionDate && (
                <>
                  <Text type="secondary">•</Text>
                  <Text type="secondary">{order.admissionDate}</Text>
                </>
              )}
            </Space>
          }
        />
      </List.Item>
    </div>
  );
}

export default function TaskOrders() {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [orders, setOrders] = useState<ITaskOrder[]>([]);
  const [stats, setStats] = useState<{
    totalAmountUSD: number;
    totalAmountTJS: number;
  }>({
    totalAmountUSD: 0,
    totalAmountTJS: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [status, setStatus] = useState<TaskOrderStatus[]>([
    TaskOrderStatus.InProgress,
    TaskOrderStatus.Pending,
    TaskOrderStatus.OnHold,
  ]);
  const [clientId, setClientId] = useState<string | undefined>(undefined);
  const [produceId, setProduceId] = useState<string | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState<boolean>(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState<boolean>(false);
  const [isAdminEditModalOpen, setIsAdminEditModalOpen] = useState<boolean>(false);
  const [selectedOrder, setSelectedOrder] = useState<ITaskOrder | undefined>(
    undefined
  );
  const [form] = Form.useForm();
  const [rejectForm] = Form.useForm();
  const [statusForm] = Form.useForm();
  const [adminEditForm] = Form.useForm();
  const { data: producesList, loading: listLoading } =
    useGetRequest(`/produce/list`);
  const { data: clientsList, loading: clientsLoading } =
    useGetRequest(`/client/list`);
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | undefined
  >([dayjs().startOf("month"), dayjs().endOf("month")]);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [orderList, setOrderList] = useState<ITaskOrder[]>([]);
  const [orderListLoading, setOrderListLoading] = useState(false);
  const [orderListSaving, setOrderListSaving] = useState(false);

  const statusColors: Record<TaskOrderStatus, string> = {
    [TaskOrderStatus.Pending]: "gold",
    [TaskOrderStatus.InProgress]: "blue",
    [TaskOrderStatus.OnHold]: "orange",
    [TaskOrderStatus.Rejected]: "red",
    [TaskOrderStatus.Completed]: "green",
  };

  const handleRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    } else {
      setDateRange(undefined);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);

    try {
      const response = await api.get("/task-order/", {
        params: {
          page,
          limit: pageSize,
          sortBy,
          order: sortOrder,
          startDate: dateRange ? dateRange[0]?.format("DD.MM.YYYY") : undefined,
          endDate: dateRange ? dateRange[1]?.format("DD.MM.YYYY") : undefined,
          status,
          clientId,
          produceId,
        },
      });

      setOrders(response.data.orders);
      setTotal(response.data.total);
      setStats({
        totalAmountUSD: response.data.totalAmountUSD,
        totalAmountTJS: response.data.totalAmountTJS,
      });
    } catch (error) {
      console.error("Error fetching orders", error);
      message.error("Ошибка при получение заказов");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [
    page,
    sortBy,
    sortOrder,
    pageSize,
    dateRange,
    status,
    clientId,
    produceId,
  ]);

  const handleSubmit = async (values: Partial<ITaskOrder>) => {
    setConfirmLoading(true);

    try {
      if (selectedOrder) {
        await api.put(`/task-order/${selectedOrder._id}`, values);
        message.success("Заказ успешно обновлен.");
      } else {
        await api.post("/task-order", values);
        message.success("Заказ успешно добавлен.");
      }
    } catch (error: any) {
      console.error("Error saving order:", error);
      message.error(error.response.data.error);
    } finally {
      setConfirmLoading(false);
      setIsModalOpen(false);
      fetchOrders();
      form.resetFields();
    }
  };

  const handleReject = async (values: { rejectReason: string }) => {
    setConfirmLoading(true);

    try {
      await api.post(`/task-order/${selectedOrder?._id}/reject`, values);
      message.success("Заказ успешно отклонен.");
      fetchOrders();
      setConfirmLoading(false);
      setIsRejectModalOpen(false);
    } catch (error: any) {
      console.error("Error rejecting order:", error);
      message.error(error.response.data.error);
      setConfirmLoading(false);
      setIsRejectModalOpen(false);
    }
  };

  const handleStatusChangeClick = (order: ITaskOrder) => {
    setSelectedOrder(order);
    setIsStatusModalOpen(true);
    statusForm.setFieldsValue({
      status: order.status,
      rejectReason: order.rejectReason,
    });
  };

  const handleStatusSubmit = async (values: { status: TaskOrderStatus; rejectReason?: string }) => {
    setConfirmLoading(true);
    try {
      await api.put(`/task-order/${selectedOrder?._id}/status`, {
        status: values.status,
        rejectReason: values.status === TaskOrderStatus.Rejected ? values.rejectReason : undefined,
      });
      message.success("Статус заказа успешно обновлен.");
      fetchOrders();
    } catch (error: any) {
      console.error("Error updating status:", error);
      message.error(error.response?.data?.error || "Не удалось обновить статус.");
    } finally {
      setConfirmLoading(false);
      setIsStatusModalOpen(false);
      statusForm.resetFields();
      setSelectedOrder(undefined);
    }
  };

  const handleEditOrder = (order: ITaskOrder) => {
    setSelectedOrder(order);
    setIsModalOpen(true);

    form.setFieldsValue({
      ...order,
    });
  };

  const handleRejectClick = (order: ITaskOrder) => {
    setSelectedOrder(order);
    setIsRejectModalOpen(true);
    rejectForm.resetFields();
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/task-order/${id}`);
      message.success("Заказ успешно удален.");
      fetchOrders();
    } catch (error: any) {
      console.error("Error deleting task order", error);
      message.error(error.response.data.error);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await api.post(`/task-order/approve/${id}`);
      message.success("Заказ успешно потвержен.");
      fetchOrders();
    } catch (error: any) {
      console.error("Error approving task order", error);
      message.error(error.response.data.error);
    }
  };

  const handleAdminEdit = (order: ITaskOrder) => {
    setSelectedOrder(order);
    setIsAdminEditModalOpen(true);
    adminEditForm.setFieldsValue({
      orderNumber: order.orderNumber,
      priority: order.priority,
      produced: order.produced,
      delivered: order.delivered,
    });
  };

  const fetchActiveOrdersForOrder = useCallback(async () => {
    setOrderListLoading(true);
    try {
      const { data } = await api.get("/task-order/", {
        params: {
          page: 1,
          limit: 500,
          sortBy: "orderNumber",
          order: "asc",
          status: ACTIVE_STATUSES,
        },
      });
      const list = data.orders || [];
      setOrderList(list);
      return list;
    } catch (e) {
      console.error(e);
      message.error("Не удалось загрузить список заказов");
      return [];
    } finally {
      setOrderListLoading(false);
    }
  }, []);

  const openOrderModal = () => {
    setIsOrderModalOpen(true);
    fetchActiveOrdersForOrder();
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleOrderDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setOrderList((items) => {
        const oldIndex = items.findIndex((o: ITaskOrder) => o._id === active.id);
        const newIndex = items.findIndex((o: ITaskOrder) => o._id === over.id);
        if (oldIndex === -1 || newIndex === -1) return items;
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSaveOrderOrder = async () => {
    if (orderList.length === 0) {
      setIsOrderModalOpen(false);
      return;
    }
    setOrderListSaving(true);
    try {
      await api.put("/task-order/order-numbers/bulk", {
        updates: orderList.map((order, index) => ({
          id: order._id,
          orderNumber: index + 1,
        })),
      });
      message.success("Порядок заказов сохранён.");
      setIsOrderModalOpen(false);
      fetchOrders();
    } catch (err: any) {
      console.error(err);
      message.error(err?.response?.data?.error || "Не удалось сохранить порядок");
    } finally {
      setOrderListSaving(false);
    }
  };

  const handleMoveOrderUp = async (order: ITaskOrder) => {
    const list = await fetchActiveOrdersForOrder();
    const idx = list.findIndex((o: ITaskOrder) => o._id === order._id);
    if (idx <= 0) return;
    const prev = list[idx - 1];
    try {
      await api.put("/task-order/order-numbers/bulk", {
        updates: [
          { id: order._id, orderNumber: idx },
          { id: prev._id, orderNumber: idx + 1 },
        ],
      });
      message.success("Заказ поднят в порядке.");
      fetchOrders();
    } catch (err: any) {
      message.error(err?.response?.data?.error || "Не удалось изменить порядок");
    }
  };

  const handleMoveOrderDown = async (order: ITaskOrder) => {
    const list = await fetchActiveOrdersForOrder();
    const idx = list.findIndex((o: ITaskOrder) => o._id === order._id);
    if (idx === -1 || idx >= list.length - 1) return;
    const next = list[idx + 1];
    try {
      await api.put("/task-order/order-numbers/bulk", {
        updates: [
          { id: order._id, orderNumber: idx + 2 },
          { id: next._id, orderNumber: idx + 1 },
        ],
      });
      message.success("Заказ опущен в порядке.");
      fetchOrders();
    } catch (err: any) {
      message.error(err?.response?.data?.error || "Не удалось изменить порядок");
    }
  };

  const handleAdminEditSubmit = async (values: { orderNumber?: number; priority: number; produced: number; delivered: number }) => {
    // Валидация на фронтенде
    if (values.delivered > values.produced) {
      message.error("Выданное количество не может превышать произведенное количество.");
      return;
    }

    if (values.produced > selectedOrder?.total!) {
      message.error("Произведенное количество не может превышать общее количество.");
      return;
    }

    if (values.delivered > selectedOrder?.total!) {
      message.error("Выданное количество не может превышать общее количество.");
      return;
    }

    if (values.orderNumber !== undefined && (values.orderNumber < 0 || !Number.isInteger(values.orderNumber))) {
      message.error("Номер заказа должен быть неотрицательным целым числом.");
      return;
    }

    setConfirmLoading(true);
    try {
      await api.put(`/task-order/${selectedOrder?._id}/admin`, values);
      message.success("Заказ успешно обновлен.");
      fetchOrders();
      setIsAdminEditModalOpen(false);
      adminEditForm.resetFields();
    } catch (error: any) {
      console.error("Error updating task order", error);
      message.error(error.response.data.error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const columns = [
    {
      title: "№",
      dataIndex: "orderNumber",
      key: "orderNumber",
      fixed: "left" as "left",
      width: 56,
      render: (val: number | undefined) => val ?? "—",
    },
    {
      title: "Дата приёма",
      dataIndex: "admissionDate",
      key: "admissionDate",
      fixed: "left" as "left",
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      fixed: "left" as "left",
      render: (value: TaskOrderStatus) => (
        <Tag color={statusColors[value] || "default"}>{value}</Tag>
      ),
    },
    {
      title: "Клиент",
      dataIndex: "clientName",
      key: "clientName",
      fixed: "left" as "left",
    },
    {
      title: "Продукция",
      dataIndex: "produceName",
      key: "produceName",
      fixed: "left" as "left",
    },
    {
      title: "Кол-во",
      dataIndex: "total",
      key: "total",
      fixed: "left" as "left",
    },
    {
      title: "Остаток",
      key: "residue",
      render: (_: any, order: ITaskOrder) => {
        const produce = order.produceId as any;
        if (produce && typeof produce === 'object' && 'residue' in produce) {
          return `${produce.residue} ${produce.unitOfMeasurement}`;
        }
        return '-';
      },
    },
    {
      title: "Остаток (2)",
      key: "residueKg",
      render: (_: any, order: ITaskOrder) => {
        const produce = order.produceId as any;
        if (produce && typeof produce === 'object' && 'residueKg' in produce) {
          const residueKg = produce.residueKg || 0;
          const unit = produce.residueKgUnit || "кг";
          return `${residueKg} ${unit}`;
        }
        return '-';
      },
    },
    {
      title: "Выдача",
      key: "delivery",
      render: (_: any, order: ITaskOrder) => (
        <div style={{ minWidth: 120 }}>
          <div style={{ marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: '#666' }}>
              {order.delivered || 0} / {order.total}
            </span>
          </div>
          <Progress
            percent={order.deliveryProgress || 0}
            size="small"
            status={order.deliveryProgress === 100 ? "success" : "active"}
            strokeColor={order.deliveryProgress === 100 ? "#52c41a" : "#1890ff"}
          />
        </div>
      ),
    },
    {
      title: "Приоритет",
      dataIndex: "priority",
      key: "priority",
      render: (priority: number) => {
        const getPriorityInfo = (p: number) => {
          if (p === 0) return { text: "Высокий", color: "red" };
          if (p <= 5) return { text: `Средний (${p})`, color: "orange" };
          return { text: `Низкий (${p})`, color: "green" };
        };

        const priorityInfo = getPriorityInfo(priority);
        return (
          <Tag color={priorityInfo.color}>
            {priorityInfo.text}
          </Tag>
        );
      },
    },

    ...(authState.user?.role !== "foreman"
      ? [
        {
          title: "Валюта",
          dataIndex: "currency",
          key: "currency",
        },
        {
          title: "Цена",
          dataIndex: "price",
          key: "price",
        },
        {
          title: "Цена со скидкой",
          dataIndex: "priceDiscounted",
          key: "priceDiscounted",
        },
        {
          title: "Скидка",
          dataIndex: "discount",
          key: "discount",
          render: (discount: number) => `${discount}%`,
        },
        {
          title: "Сумма",
          dataIndex: "totalCost",
          key: "totalCost",
        },
      ]
      : []),
    {
      title: "Описание",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Причина откаха",
      dataIndex: "rejectReason",
      key: "rejectReason",
    },
    {
      title: "Пользователь",
      dataIndex: "ownerName",
      key: "ownerName",
    },
    {
      title: "",
      key: "actions",
      render: (_: any, order: ITaskOrder) => {
        const hasOrderNumber = order.orderNumber != null && ACTIVE_STATUSES.includes(order.status);
        const menu = (
          <Menu>
            {authState.user?.role === UserRole.Admin && hasOrderNumber && (
              <>
                <Menu.Item
                  icon={<UpOutlined />}
                  onClick={() => handleMoveOrderUp(order)}
                  disabled={order.orderNumber === 1}
                >
                  Поднять в порядке
                </Menu.Item>
                <Menu.Item
                  icon={<DownOutlined />}
                  onClick={() => handleMoveOrderDown(order)}
                >
                  Опустить в порядке
                </Menu.Item>
                <Menu.Divider />
              </>
            )}
            {canEdit(authState.user?.role) && order.status === TaskOrderStatus.Pending && (
              <Popconfirm
                title="Вы уверены, что хотите потвердить этот заказ?"
                okText="Да"
                cancelText="Нет"
                onConfirm={() => handleApprove(order._id)}
              >
                <Menu.Item>Потвердить</Menu.Item>
              </Popconfirm>
            )}

            {canEdit(authState.user?.role) && (
              <Menu.Item onClick={() => handleEditOrder(order)}>
                Изменить
              </Menu.Item>
            )}

            {authState.user?.role === UserRole.Admin && (
              <Menu.Item onClick={() => handleAdminEdit(order)}>
                Админ редактирование
              </Menu.Item>
            )}

            {(canEdit(authState.user?.role) ||
              authState.user?.role === UserRole.SalesDept ||
              authState.user?.role === UserRole.Foreman ||
              authState.user?.role === UserRole.Director) && (
                <Menu.Item onClick={() => handleStatusChangeClick(order)}>
                  Изменить статус
                </Menu.Item>
              )}

            {(canEdit(authState.user?.role) ||
              authState.user?.role === UserRole.SalesDept) && (
                <Menu.Item onClick={() => handleRejectClick(order)}>
                  Отклонить
                </Menu.Item>
              )}

            {canEdit(authState.user?.role) && (
              <Popconfirm
                title="Вы уверены, что хотите удалить этот заказ?"
                okText="Да"
                cancelText="Нет"
                onConfirm={() => handleDelete(order._id)}
              >
                <Menu.Item>Удалить</Menu.Item>
              </Popconfirm>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const rowClassName = (record: ITaskOrder) => {
    if (record.status === TaskOrderStatus.Completed) {
      return "row-green";
    }

    if (record.status === TaskOrderStatus.Rejected) {
      return "row-red";
    }

    if (record.status === TaskOrderStatus.OnHold) {
      return "row-yellow";
    }

    return "";
  };

  return (
    <div style={{ padding: 24 }}>
      <Title>Задачи-Заказы</Title>
      {stats && (
        <Row gutter={[16, 16]} justify="start">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card hoverable>
              <Statistic
                title="Сумму в USD"
                value={stats.totalAmountUSD}
                prefix="$"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card hoverable>
              <Statistic title="Сумму в TJS" value={stats.totalAmountTJS} />
            </Card>
          </Col>
        </Row>
      )}
      <Space style={{ marginBottom: 16, marginTop: 16 }} wrap>
        <RangePicker
          onChange={handleRangeChange}
          placeholder={["Начало", "Конец"]}
          format="DD.MM.YY"
          value={dateRange}
        />
        <Select
          placeholder="Фильтр по статусу"
          onChange={(value) => setStatus(value)}
          allowClear
          style={{ width: 200 }}
          mode="multiple"
          defaultValue={[TaskOrderStatus.InProgress, TaskOrderStatus.Pending, TaskOrderStatus.OnHold]}
        >
          {Object.values(TaskOrderStatus).map((status) => {
            if (authState.user?.role === "foreman") {
              if (
                status === TaskOrderStatus.Pending ||
                status === TaskOrderStatus.InProgress ||
                status === TaskOrderStatus.OnHold
              ) {
                return (
                  <Option key={status} value={status}>
                    {status}
                  </Option>
                );
              }
            } else {
              return (
                <Option key={status} value={status}>
                  {status}
                </Option>
              );
            }
          })}
        </Select>

        <Select
          defaultValue="desc"
          onChange={(value: "asc" | "desc") => setSortOrder(value)}
          style={{ width: 150 }}
        >
          <Option value="asc">По возрастанию</Option>
          <Option value="desc">По убыванию</Option>
        </Select>

        <Select
          placeholder="Сортировать по"
          defaultValue="createdAt"
          onChange={(value) => setSortBy(value)}
          style={{ width: 200 }}
        >
          <Option value="orderNumber">По номеру</Option>
          <Option value="createdAt">Дата приёма</Option>
          <Option value="tanningDate">Дата загатовки</Option>
          <Option value="givenDate">Дата выдачи</Option>
          <Option value="totalCost">Сумма</Option>
          <Option value="total">Кол-во</Option>
        </Select>

        <Select
          placeholder="Фильтр по клиенту"
          loading={clientsLoading}
          onChange={(value) => setClientId(value)}
          style={{ width: 300 }}
          allowClear
          showSearch
          filterOption={(input, option) => {
            const optionText = option?.label || option?.children;
            return String(optionText).toLowerCase().includes(input.toLowerCase());
          }}
          optionFilterProp="children"
        >
          {clientsList?.map((client: Client) => (
            <Option key={client._id} value={client._id}>
              {client.name} - {client.clientType}
            </Option>
          ))}
        </Select>

        <Select
          placeholder="Фильтр по продукции"
          loading={listLoading}
          onChange={(value) => setProduceId(value)}
          style={{ width: 300 }}
          allowClear
          showSearch
          filterOption={(input, option) => {
            const optionText = option?.label || option?.children;
            return String(optionText).toLowerCase().includes(input.toLowerCase());
          }}
          optionFilterProp="children"
        >
          {producesList?.map((produce: Produce) => (
            <Option key={produce._id} value={produce._id}>
              {produce.name}
            </Option>
          ))}
        </Select>
        {(canEdit(authState.user?.role) ||
          authState.user?.role === UserRole.SalesDept || authState.user?.role === UserRole.Director || authState.user?.role === UserRole.Foreman) && (
            <Button type="primary" onClick={() => setIsModalOpen(true)}>
              Добавить
            </Button>
          )}
        {authState.user?.role === UserRole.Admin && (
          <Button icon={<UnorderedListOutlined />} onClick={openOrderModal}>
            Порядок заказов
          </Button>
        )}
      </Space>

      <Modal
        title="Порядок заказов"
        open={isOrderModalOpen}
        onCancel={() => setIsOrderModalOpen(false)}
        width={640}
        footer={[
          <Button key="cancel" onClick={() => setIsOrderModalOpen(false)}>
            Отмена
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={orderListSaving}
            onClick={handleSaveOrderOrder}
            disabled={orderList.length === 0}
          >
            Сохранить порядок
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16, color: "#666" }}>
          Перетащите заказы для изменения порядка. Нумерация только для активных (в ожидании, в процессе, в стопе).
        </div>
        {orderListLoading ? (
          <div style={{ textAlign: "center", padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : orderList.length === 0 ? (
          <div style={{ textAlign: "center", padding: 48, color: "#999" }}>
            Нет активных заказов для сортировки
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleOrderDragEnd}
          >
            <SortableContext
              items={orderList.map((o) => o._id)}
              strategy={verticalListSortingStrategy}
            >
              <List
                dataSource={orderList}
                renderItem={(order, index) => (
                  <SortableOrderRow
                    key={order._id}
                    order={order}
                    index={index}
                    statusColors={statusColors}
                  />
                )}
              />
            </SortableContext>
          </DndContext>
        )}
      </Modal>

      <Table
        columns={columns}
        dataSource={orders}
        rowKey={(record) => record._id}
        rowClassName={rowClassName}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
          showSizeChanger: true,
        }}
        scroll={{ x: "max-content" }}
        loading={loading}
      />

      <Modal
        title="Добавить заказ"
        open={isModalOpen}
        confirmLoading={confirmLoading}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {!selectedOrder && (
            <>
              <Form.Item
                name="produceId"
                label="Продукция"
                rules={[{ required: true, message: "Выберите продукцию" }]}
              >
                <Select
                  placeholder="Выберите продукции"
                  loading={listLoading}
                  showSearch
                  filterOption={(input, option) => {
                    const optionText = option?.label || option?.children;
                    return String(optionText).toLowerCase().includes(input.toLowerCase());
                  }}
                  optionFilterProp="children"
                >
                  {producesList?.map((produce: Produce) => (
                    <Option key={produce._id} value={produce._id}>
                      {produce.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="total"
                label="Кол-во"
                rules={[
                  { required: true, message: "Пожалуйста, введите кол-во" },
                ]}
              >
                <InputNumber style={{ width: 200 }} />
              </Form.Item>
              <Form.Item
                name="price"
                label="Цена"
                rules={[
                  { required: true, message: "Пожалуйста, введите цену" },
                ]}
              >
                <InputNumber style={{ width: 200 }} />
              </Form.Item>
              <Form.Item name="discount" label="Скидка">
                <InputNumber style={{ width: 200 }} min={0} max={100} />
              </Form.Item>
              <Form.Item
                name="currency"
                label="Валюта"
                rules={[{ required: true, message: "Выберите валюту" }]}
              >
                <Select placeholder="Выберите валюту">
                  {Object.values(Currency).map((currency) => (
                    <Option key={currency} value={currency}>
                      {currency}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="clientId"
                label="Клиент"
                rules={[{ required: true, message: "Выберите клиента" }]}
              >
                <Select
                  placeholder="Выберите клиента"
                  loading={clientsLoading}
                  showSearch
                  filterOption={(input, option) => {
                    const optionText = option?.label || option?.children;
                    return String(optionText).toLowerCase().includes(input.toLowerCase());
                  }}
                  optionFilterProp="children"
                >
                  {clientsList?.map((client: Client) => (
                    <Option key={client._id} value={client._id}>
                      {client.name} - {client.clientType}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="whatsappNumber"
                label="Номер WhatsApp"
                rules={[
                  {
                    message: "Используйте формат 998XXXXXXXXX"
                  }
                ]}
              >
                <Input
                  placeholder="998024352525"
                  style={{ width: 200 }}
                />
              </Form.Item>
            </>
          )}
          <Form.Item
            name="description"
            label="Описание"
            rules={[
              { required: true, message: "Пожалуйста, введите описание" },
            ]}
          >
            <Input.TextArea />
          </Form.Item>

        </Form>
      </Modal>

      <Modal
        title="Отказ"
        open={isRejectModalOpen}
        confirmLoading={confirmLoading}
        onCancel={() => setIsRejectModalOpen(false)}
        onOk={() => rejectForm.submit()}
      >
        <Form form={rejectForm} layout="vertical" onFinish={handleReject}>
          <Form.Item
            name="rejectReason"
            label="Причина отказа"
            rules={[
              { required: true, message: "Пожалуйста, введите причину отказа" },
            ]}
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Изменить статус"
        open={isStatusModalOpen}
        confirmLoading={confirmLoading}
        onCancel={() => {
          setIsStatusModalOpen(false);
          statusForm.resetFields();
        }}
        onOk={() => statusForm.submit()}
      >
        <Form form={statusForm} layout="vertical" onFinish={handleStatusSubmit}>
          <Form.Item
            name="status"
            label="Статус"
            rules={[{ required: true, message: "Выберите статус" }]}
          >
            <Select placeholder="Выберите статус">
              {Object.values(TaskOrderStatus).map((status) => (
                <Option key={status} value={status}>
                  {status}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.status !== currentValues.status}>
            {({ getFieldValue }) =>
              getFieldValue("status") === TaskOrderStatus.Rejected ? (
                <Form.Item
                  name="rejectReason"
                  label="Причина отказа"
                  rules={[{ required: true, message: "Пожалуйста, введите причину отказа" }]}
                >
                  <Input.TextArea />
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Админ редактирование"
        open={isAdminEditModalOpen}
        confirmLoading={confirmLoading}
        onCancel={() => {
          setIsAdminEditModalOpen(false);
          adminEditForm.resetFields();
        }}
        onOk={() => adminEditForm.submit()}
      >
        <Form form={adminEditForm} layout="vertical" onFinish={handleAdminEditSubmit}>
          <Form.Item
            name="orderNumber"
            label="Номер (порядок заказа)"
            tooltip="Порядковый номер для отображения и сортировки. Можно менять для изменения порядка заказов."
          >
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Авто"
              min={0}
              step={1}
            />
          </Form.Item>

          <Form.Item
            name="priority"
            label="Приоритет"
            rules={[
              { required: true, message: "Пожалуйста, выберите приоритет" },
            ]}
          >
            <Select
              style={{ width: "100%" }}
              placeholder="Выберите приоритет"
            >
              <Option value={0}>Высокий (0)</Option>
              <Option value={1}>Средний (1)</Option>
              <Option value={2}>Средний (2)</Option>
              <Option value={3}>Средний (3)</Option>
              <Option value={4}>Средний (4)</Option>
              <Option value={5}>Средний (5)</Option>
              <Option value={6}>Низкий (6)</Option>
              <Option value={7}>Низкий (7)</Option>
              <Option value={8}>Низкий (8)</Option>
              <Option value={9}>Низкий (9)</Option>
              <Option value={10}>Низкий (10)</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="produced"
            label="Произведено"
            rules={[
              { required: true, message: "Пожалуйста, введите количество произведенного" },
              { type: "number", min: 0, message: "Количество не может быть отрицательным" },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Количество произведенного"
              min={0}
              max={selectedOrder?.total}
            />
          </Form.Item>

          <Form.Item
            name="delivered"
            label="Выдано"
            rules={[
              { required: true, message: "Пожалуйста, введите количество выданного" },
              { type: "number", min: 0, message: "Количество не может быть отрицательным" },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Количество выданного"
              min={0}
              max={selectedOrder?.total}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
